import React, { createContext, useContext, useReducer, useEffect, useRef, ReactNode } from 'react';
import { api, Product } from '../services/api';
import { useUser } from './UserContext';

// Guest favorites are just a list of product IDs in localStorage.
const GUEST_FAVORITES_KEY = 'ecofashion_guest_favorites';

const saveGuestFavoriteIds = (productIds: number[]) => {
  localStorage.setItem(GUEST_FAVORITES_KEY, JSON.stringify(productIds));
};

const loadGuestFavoriteIds = (): number[] => {
  try {
    const saved = localStorage.getItem(GUEST_FAVORITES_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

interface FavoritesState {
  items: Product[];
  loading: boolean;
  error: string | null;
  isGuest: boolean;
}

type FavoritesAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ITEMS'; payload: Product[] }
  | { type: 'ADD_ITEM'; payload: Product }
  | { type: 'REMOVE_ITEM'; payload: number }
  | { type: 'SET_GUEST_MODE'; payload: boolean };

const initialState: FavoritesState = {
  items: [],
  loading: false,
  error: null,
  isGuest: false,
};

function favoritesReducer(state: FavoritesState, action: FavoritesAction): FavoritesState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_ITEMS':
      return { ...state, items: action.payload };
    case 'ADD_ITEM':
      if (state.items.some(product => product.id === action.payload.id)) {
        return state;
      }
      return { ...state, items: [...state.items, action.payload] };
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter(product => product.id !== action.payload) };
    case 'SET_GUEST_MODE':
      return { ...state, isGuest: action.payload };
    default:
      return state;
  }
}

interface FavoritesContextType {
  state: FavoritesState;
  isFavorite: (productId: number) => boolean;
  toggleFavorite: (productId: number) => Promise<void>;
  loadFavorites: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(favoritesReducer, initialState);

  const loadFavorites = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      if (api.isAuthenticated()) {
        dispatch({ type: 'SET_GUEST_MODE', payload: false });
        const favorites = await api.getFavorites();
        dispatch({ type: 'SET_ITEMS', payload: favorites });
      } else {
        dispatch({ type: 'SET_GUEST_MODE', payload: true });
        const guestIds = loadGuestFavoriteIds();

        const products: Product[] = [];
        for (const productId of guestIds) {
          try {
            products.push(await api.getProduct(productId));
          } catch (error) {
            console.error(`Failed to load product ${productId}:`, error);
          }
        }
        dispatch({ type: 'SET_ITEMS', payload: products });
      }
    } catch (error: any) {
      console.error('Failed to load favorites:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to load favorites' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const isFavorite = (productId: number) => state.items.some(product => product.id === productId);

  const toggleFavorite = async (productId: number) => {
    const currentlyFavorite = isFavorite(productId);

    try {
      dispatch({ type: 'SET_ERROR', payload: null });

      if (api.isAuthenticated()) {
        if (currentlyFavorite) {
          await api.removeFromFavorites(productId);
          dispatch({ type: 'REMOVE_ITEM', payload: productId });
        } else {
          const response = await api.addToFavorites(productId);
          dispatch({ type: 'ADD_ITEM', payload: response.favorite });
        }
      } else {
        const guestIds = loadGuestFavoriteIds();

        if (currentlyFavorite) {
          saveGuestFavoriteIds(guestIds.filter(id => id !== productId));
          dispatch({ type: 'REMOVE_ITEM', payload: productId });
        } else {
          saveGuestFavoriteIds([...guestIds, productId]);
          const product = await api.getProduct(productId);
          dispatch({ type: 'ADD_ITEM', payload: product });
        }
      }
    } catch (error: any) {
      console.error('Failed to update favorites:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to update favorites' });
      throw error;
    }
  };

  useEffect(() => {
    loadFavorites();
  }, []);

  // When a guest logs in (or registers) mid-session, merge whatever was in
  // their localStorage favorites into their account, then reload from the server.
  const { isAuthenticated } = useUser();
  const wasAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    const wasAuthenticated = wasAuthenticatedRef.current;
    wasAuthenticatedRef.current = isAuthenticated;

    if (!wasAuthenticated && isAuthenticated) {
      const guestIds = loadGuestFavoriteIds();

      if (guestIds.length === 0) {
        loadFavorites();
        return;
      }

      (async () => {
        for (const productId of guestIds) {
          try {
            await api.addToFavorites(productId);
          } catch (error) {
            // Item may already be favorited on the account — ignore and continue.
            console.error('Failed to merge guest favorite:', productId, error);
          }
        }
        localStorage.removeItem(GUEST_FAVORITES_KEY);
        await loadFavorites();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const contextValue: FavoritesContextType = {
    state,
    isFavorite,
    toggleFavorite,
    loadFavorites,
  };

  return (
    <FavoritesContext.Provider value={contextValue}>
      {children}
    </FavoritesContext.Provider>
  );
};
